import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  SearchOff as SearchOffIcon,
} from '@mui/icons-material';
import { ROUTES } from '../config/constants';
import { useAuthStore } from '../store/authStore';

export const NotFound = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const handleGoHome = () => {
    navigate(isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 6 },
            borderRadius: 2,
            maxWidth: 600,
            width: '100%',
          }}
        >
          {/* 404 Icon */}
          <SearchOffIcon
            sx={{
              fontSize: { xs: 80, sm: 120 },
              color: 'primary.main',
              mb: 2,
              opacity: 0.8,
            }}
          />

          {/* 404 Text */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '4rem', sm: '6rem' },
              fontWeight: 'bold',
              color: 'text.primary',
              mb: 2,
            }}
          >
            404
          </Typography>

          {/* Main Message */}
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Page Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            paragraph
            sx={{ mb: 4, px: { xs: 0, sm: 4 } }}
          >
            Oops! The page you're looking for doesn't exist. It might have been moved or deleted,
            or you may have mistyped the URL.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Go to Home'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
            >
              Go Back
            </Button>
          </Box>

          {/* Helpful Links */}
          {isAuthenticated && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quick Links:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  mt: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={() => navigate(ROUTES.PROJECTS)}
                >
                  My Projects
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate('/podcasts')}
                >
                  Search Podcasts
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate(ROUTES.SETTINGS)}
                >
                  Settings
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Additional Help Text */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3 }}
        >
          If you believe this is an error, please contact support.
        </Typography>
      </Box>
    </Container>
  );
};
